import React, { useEffect, useRef } from 'react';
import { HdIconButton } from '../../../button/components/icon-button/HdIconButton';
import HdIcon from '../../../media/components/icon/HdIcon';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import styles from './styles.module.scss';
import { HdPane } from '../../../components';
import QueryViewer from '../../../code/components/query-viewer/QueryViewer';
import HdModal from '../../../modal/components/modal/HdModal';
import ScrollToTopButton from '../../../a11y/scroll/components/scroll-to-top-button/ScrollToTopButton';
import { ActivityLogListContainer } from '../../activity-log/ActivityLogListContainer';
import { useActivityLog } from '../../activity-log/useActivityLog';
import { SyncHistoryItem } from '../ActivationSyncHistory/models/sync-history-item';
import { HdCoordinatedHeight } from '../../../layout/components/coordinated-height/HdCoordinatedHeight';

interface ActivationHistoryItemDialogProps {
  activationSeqId: number;
  activationSyncHistoryItem: SyncHistoryItem;
  open: boolean;
  onClose: () => void;
}

function ActivationSyncHistoryItemDialog({
  activationSeqId,
  activationSyncHistoryItem,
  open,
  onClose
}: ActivationHistoryItemDialogProps) {
  const coordinatedHeightTargetRef = useRef(null);

  const {
    state,
    loadingMore,
    showActivities,
    showSkeleton,
    noActivitiesFound,
    sentryRef,
    refresh
  } = useActivityLog({
    loadFilters: false,
    entityFilters: {
      entities_l0: [
        {
          type: 'ACTIVATION',
          id: activationSeqId,
          entities_l1: [
            {
              type: 'ACTIVATION_RUN',
              id: activationSyncHistoryItem.id
            }
          ]
        }
      ]
    }
  });

  useEffect(() => {
    if (open) {
      refresh();
    }
  }, [open]);

  const dataIdGenerator = getDataIdGenerator('history-item-dialog');

  return (
    <HdModal
      open={open}
      variant='custom'
      onClose={onClose}
      placement='top'
      styling={{ width: '800px', marginTop: '60px' }}
    >
      <div data-id={dataIdGenerator('')}>
        <div className='dialog-header'>
          <div className='dialog-title'>Activation Sync Details</div>

          <HdIconButton onClick={onClose} dataId={dataIdGenerator('close')}>
            <HdIcon name='close' className={styles.closeIcon} size={3} />
          </HdIconButton>
        </div>

        <HdCoordinatedHeight elRef={coordinatedHeightTargetRef.current}>
          <div
            className={styles.dialogBody}
            id={dataIdGenerator('')}
            ref={coordinatedHeightTargetRef}
          >
            <div className='dialog-body p-6' id='box_body'>
              {activationSyncHistoryItem?.failureMsg && (
                <HdPane variant='error-faded' className='mb-5'>
                  <div className='d-flex align-items-start'>
                    <HdIcon
                      name='error-filled'
                      size={3}
                      className='mr-2 text-error icon-with-text'
                    />

                    <span className='text-body-3 mr-1 text-default'>Activation sync failed:</span>

                    <span className='text-body-1 text-default'>
                      {activationSyncHistoryItem.failureMsg}
                    </span>
                  </div>
                </HdPane>
              )}

              <div className='text-body-3 mb-3'>SQL Query</div>

              {activationSyncHistoryItem?.query && (
                <QueryViewer
                  value={activationSyncHistoryItem?.query}
                  dataId={dataIdGenerator('')}
                />
              )}

              <div className={styles.logContainer}>
                <div className='center-flex-row justify-between mt-4 mb-2'>
                  <span className='text-body-3'>Activity Logs</span>

                  <HdIconButton
                    verticalEdge='both'
                    horizontalEdge='both'
                    onClick={refresh}
                    dataId={dataIdGenerator('refresh')}
                  >
                    <HdIcon
                      size={3}
                      name='refresh'
                      className={`refresh-icon ${state.fetchingList ? 'refreshing' : ''}`}
                    />
                  </HdIconButton>
                </div>

                <ActivityLogListContainer
                  state={state}
                  sentryRef={sentryRef}
                  noActivitiesFound={noActivitiesFound}
                  showSkeleton={showSkeleton}
                  showActivities={showActivities}
                  loadingMore={loadingMore}
                  refresh={refresh}
                />
              </div>
            </div>

            <ScrollToTopButton scrollViewportId={dataIdGenerator('')} />
          </div>
        </HdCoordinatedHeight>
      </div>
    </HdModal>
  );
}

export default ActivationSyncHistoryItemDialog;
